/**
 * Pricedown Font Ligature Substitution
 * 
 * This script handles special character combinations in the Pricedown font
 * that require custom ligature handling for proper display in web browsers.
 * 
 * The GTA font style has specific ligatures for combinations like "tm", "ft", etc.
 * that may not render correctly with standard browser OpenType support.
 */

// Map of character combinations to their special ligature representations
const LIGATURE_MAP = {
    'tm': 'ᵗᵐ', // Using superscript characters as fallback
    'ft': 'ᶠᵗ',
    'fi': 'ﬁ',  // Unicode has some standard ligatures
    'fl': 'ﬂ',
    'ff': 'ﬀ',
    'ffi': 'ﬃ',
    'ffl': 'ﬄ'
    // Add more mappings as needed
};

// Special GTA-style character combinations that need custom handling
const GTA_SPECIAL_COMBINATIONS = [
    'tm', 'ft', 'fi', 'fl', 'ff', 'ffi', 'ffl'
];

/**
 * Process text to replace character combinations with their ligature equivalents
 * @param {string} text - The input text to process
 * @return {string} - Text with ligature substitutions applied
 */
function applyLigatures(text) {
    if (!text) return text;
    
    let processedText = text;
    
    // Apply direct character substitutions from the map
    for (const [combo, replacement] of Object.entries(LIGATURE_MAP)) {
        // Use a regex with word boundary to avoid replacing within words where not appropriate
        const regex = new RegExp(combo, 'gi');
        processedText = processedText.replace(regex, replacement);
    }
    
    // For more complex cases, we could use HTML spans with custom classes
    // This would allow more precise styling for specific character combinations
    
    return processedText;
}

/**
 * Apply special styling to GTA-style character combinations using HTML
 * This approach uses spans with custom classes for more precise control
 * @param {string} text - The input text to process
 * @return {string} - HTML with styled spans for special combinations
 */
function applyGtaStyleLigatures(text) {
    if (!text) return text;
    
    let processedText = text;
    
    // For the 'tm' combination specifically, we'll use a custom span
    processedText = processedText.replace(/tm/gi, '<span class="gta-ligature gta-tm">tm</span>');
    
    // Add more custom replacements as needed
    
    return processedText;
}

/**
 * Initialize ligature handling for specified elements
 * @param {Object} options - Configuration options
 */
function initLigatureHandling(options = {}) {
    // Default selectors for title elements
    const titleSelector = options.titleSelector || '#map-title-preview';
    const titleInputSelector = options.titleInputSelector || '#map-title-input';
    
    // Get the elements
    const titleElement = document.querySelector(titleSelector);
    const titleInputElement = document.querySelector(titleInputSelector);
    
    if (!titleElement || !titleInputElement) {
        console.error('Ligature handling: Required elements not found');
        return;
    }
    
    // Initial processing of existing text
    processElementText(titleElement, titleInputElement.value);
    
    // Set up event listener for input changes
    titleInputElement.addEventListener('input', (e) => {
        processElementText(titleElement, e.target.value);
    });
    
    console.log('Ligature handling initialized for', titleSelector);
}

/**
 * Process and update element text with ligature substitutions
 * @param {HTMLElement} element - The element to update
 * @param {string} text - The input text to process
 */
function processElementText(element, text) {
    // Apply ligature substitutions
    const processedText = applyLigatures(text);
    
    // Update the element
    // If using HTML spans, use innerHTML, otherwise use textContent
    element.textContent = processedText;
}

// Export functions for use in main.js
window.FontLigatures = {
    applyLigatures,
    applyGtaStyleLigatures,
    initLigatureHandling
};

console.log('Font ligature handling module loaded');
